import React from 'react';
import PropTypes from 'prop-types';
import {useServicePriceContext} from "@contexts/ContextServicePriceProvider";
import {FormatPrice} from "@helpers/HelperFunc";
import language from "@lang/language";
import PriceDefault from "./layouts/PriceDefault";
import PriceA from "./layouts/PriceA";
import PriceB from "./layouts/PriceB";
import PriceC from "./layouts/PriceC";
import PriceD from "./layouts/PriceD";
import PriceE from "./layouts/PriceE";
import PriceF from "./layouts/PriceF";
import PriceG from "./layouts/PriceG";
import PriceH from "./layouts/PriceH";
import PriceI from "./layouts/PriceI";
import PriceText from "./layouts/PriceText";

const Price = ({
				   serviceId,
				   term = 12,
				   type = 'new', // new, renew
				   layout = '', // block style layout
				   color = '', // text color
				   emptyReturn = null, // return some text if no price found
				   showMonthly = false
			   }) => {

	if (!serviceId) {
		return emptyReturn;
	}

	const contextPrice = useServicePriceContext();
	const priceData = getPriceData(contextPrice, serviceId, term, type, showMonthly);

	if (!priceData) {
		return emptyReturn;
	}

	const layouts = {
		PriceDefault, PriceA, PriceB, PriceC, PriceD, PriceE,
		PriceF, PriceG, PriceH, PriceI, PriceText
	};
	const LayoutComponent = layouts[layout] ?? PriceDefault;

	return (
	  <LayoutComponent
		priceData={priceData}
		color={color}
		showMonthly={showMonthly}
		term={term}
	  />
	);
};

// Get prices from context and format them
///////////////////////////////////////////////////////////////////////
function getPriceData(contextPrice, serviceId, term, type, showMonthly) {

	const priceDataAll = contextPrice.getForId(serviceId);

	if (!priceDataAll || (typeof priceDataAll === 'object' && Object.keys(priceDataAll).length === 0) || !priceDataAll[term]) {
		return null;
	}

	let priceData = {...priceDataAll[term]};
	let prices = {};

	let priceKey = showMonthly === true ? 'monthlyPriceNew' : 'priceNew';
	let basePriceKey = showMonthly === true ? 'monthlyBasePriceNew' : 'basePriceNew';
	let priceOriginalKey = 'priceNew';
	let basePriceOriginalKey = 'basePriceNew';

	if (type === 'renew') {
		priceKey = showMonthly === true ? 'monthlyPriceRenew' : 'priceRenew';
		basePriceKey = showMonthly === true ? 'monthlyBasePriceRenew' : 'basePriceRenew';
		priceOriginalKey = 'priceRenew';
		basePriceOriginalKey = 'basePriceRenew';
	}

	if (serviceId === 'plan_gomba_shop_01' || serviceId === 'plan_gomba_shop_02' || serviceId === 'plan_gomba_shop_03') {
		let priceDataSupport = contextPrice.getForId("shopiko_support_01");
		priceDataSupport = {...priceDataSupport[term]};

		prices = {
			price: priceData[priceKey] = FormatPrice(priceData[priceKey], priceDataSupport[priceKey]),
			basePrice: priceData[basePriceKey] = FormatPrice(priceData[basePriceKey], priceDataSupport[basePriceKey]),
			priceOriginal: priceData[priceOriginalKey] = FormatPrice(priceData[priceOriginalKey], priceDataSupport[priceOriginalKey]),
			basePriceOriginal: priceData[basePriceOriginalKey] = FormatPrice(priceData[basePriceOriginalKey], priceDataSupport[basePriceOriginalKey]),
			text: showMonthly ? language.eurM : language.eurY,
			curr: language.evro,
			serviceName: priceData.serviceName
		}
	} else {
		prices = {
			price: priceData[priceKey] = FormatPrice(priceData[priceKey]),
			basePrice: priceData[basePriceKey] = FormatPrice(priceData[basePriceKey]),
			priceOriginal: priceData[priceOriginalKey] = FormatPrice(priceData[priceOriginalKey]),
			basePriceOriginal: priceData[basePriceOriginalKey] = FormatPrice(priceData[basePriceOriginalKey]),
			text: showMonthly ? language.eurM : language.eurY,
			curr: language.evro,
			serviceName: priceData.serviceName
		}
	}

	if (prices.price === '0') {
		return null;
	}

	return prices;
}

// Return price ID for given service ID and term
///////////////////////////////////////////////////////////////////////
export function getPriceId(serviceId, term = 12) {
	const contextPrice = useServicePriceContext();
	const priceDataAll = contextPrice.getForId(serviceId);

	return priceDataAll?.[term]?.id ?? '';
}

// Hook version
export function useGetPriceId() {
	const contextPrice = useServicePriceContext();

	return (serviceId, term = 12) => {
		if (!serviceId) {
			return '';
		}

		const priceDataAll = contextPrice.getForId(serviceId);
		return priceDataAll?.[term]?.id ?? '';
	};
}

///////////////////////////////////////////////////////////////////////

Price.propTypes = {
	serviceId: PropTypes.string,
	term: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.number
	]),
	type: PropTypes.string,
	layout: PropTypes.string,
	color: PropTypes.string,
	emptyReturn: PropTypes.oneOfType([
		PropTypes.string,
		PropTypes.bool
	]),
	showMonthly: PropTypes.bool
};

export default Price;
